import { Link } from "react-router-dom";
import { Handshake, ShieldCheck, Sparkles, MessageCircle, MapPin } from "lucide-react";
import Logo from "../components/Logo";
import { waLink, waNumberDisplay } from "../lib/wa";

const FOUNDER_IMG =
  "https://images.unsplash.com/photo-1579389248774-07907f421a6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxmcmllbmRseSUyMHByb2Zlc3Npb25hbCUyMGhlbHBpbmclMjBwZXJzb258ZW58MHx8fHwxNzg2Njk5NDk2fDA&ixlib=rb-4.1.0&q=85";

export default function About() {
  return (
    <div className="max-w-6xl mx-auto px-5 md:px-8 pt-14 md:pt-20 pb-24">
      {/* Hero */}
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 fade-up">
          <span className="label-eyebrow">our story</span>
          <h1 className="mt-4 font-display text-5xl md:text-6xl font-bold leading-[1] tracking-tight" style={{ color: "var(--primary)" }}>
            Real people. <br />
            <span className="italic" style={{ color: "var(--secondary)" }}>Real help.</span>
          </h1>
          <p className="mt-6 text-lg leading-relaxed max-w-xl" style={{ color: "var(--text-muted)" }}>
            HeylpMate exists because everyday life needs a helping hand — and finding a trustworthy person at the right time is harder than it should be.
          </p>
        </div>
        <div className="lg:col-span-5 relative">
          <img src={FOUNDER_IMG} alt="Founder story" className="rounded-3xl border border-black/10 shadow-[0_20px_60px_-20px_rgba(29,46,95,0.35)] object-cover w-full max-h-[420px]" />
        </div>
      </div>

      {/* Founder note */}
      <section className="mt-20 md:mt-28 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <Logo />
          <div className="label-eyebrow mt-6">a note from the founder</div>
          <h2 className="font-display text-3xl mt-3 leading-tight" style={{ color: "var(--primary)" }}>
            Why HeylpMate?
          </h2>
        </div>
        <div className="lg:col-span-8 rounded-3xl border border-black/10 bg-white p-8 md:p-12 card-lift">
          <div className="space-y-5 text-[17px] leading-relaxed" style={{ color: "var(--text-main)" }}>
            <p>
              I started HeylpMate with a simple idea: <b>people often need practical, trustworthy human assistance</b>,
              but finding the right person at the right time is difficult.
            </p>
            <p>
              HeylpMate is designed to connect customers with <b>verified human Mates</b> for everyday needs — shopping,
              railway and airport assistance, hospital visits, luggage, personal tasks, elderly and baby care,
              corporate support, and more.
            </p>
            <p>
              The vision is simple: make human assistance <b>easy to book, affordable, reliable, and available</b>
              whenever and wherever people need it.
            </p>
          </div>
          <div className="mt-10 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full grid place-items-center" style={{ background: "var(--primary)" }}>
              <Handshake className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display text-lg" style={{ color: "var(--primary)" }}>The HeylpMate Founder</div>
              <div className="text-xs uppercase tracking-widest text-neutral-500">Founder, HeylpMate · Pune</div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="mt-20 md:mt-28">
        <span className="label-eyebrow">what we stand for</span>
        <h2 className="font-display text-4xl md:text-5xl font-bold mt-3 leading-tight">Three promises to every customer.</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {[
            { i: ShieldCheck, t: "Verified humans", d: "Every Mate is ID-checked and trained. No gigs without accountability." },
            { i: Sparkles, t: "Transparent price", d: "See platform commission and Mate earnings on every booking — no hidden fees." },
            { i: MessageCircle, t: "Real conversations", d: "Talk to us on WhatsApp any hour. Priority members get instant response." },
          ].map(({ i: I, t, d }) => (
            <div key={t} className="rounded-3xl border border-black/10 bg-white p-6 card-lift">
              <div className="w-11 h-11 rounded-full grid place-items-center" style={{ background: "var(--secondary)" }}>
                <I className="w-5 h-5 text-white" />
              </div>
              <div className="font-display text-2xl mt-5" style={{ color: "var(--primary)" }}>{t}</div>
              <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="mt-20 md:mt-28 relative rounded-3xl overflow-hidden text-white p-10 md:p-16" style={{ background: "var(--primary)" }}>
        <div className="grain absolute inset-0 opacity-25" />
        <div className="relative grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-7">
            <span className="text-xs uppercase tracking-widest font-bold" style={{ color: "var(--secondary)" }}>get in touch</span>
            <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
              Talk to a real human, not a form.
            </h2>
            <p className="mt-4 text-white/80 max-w-md leading-relaxed">
              Questions, feedback, partnerships, or want to be a Mate? WhatsApp us — a real person will reply.
            </p>
            <a
              href={waLink("Hi HeylpMate! ")}
              target="_blank" rel="noreferrer noopener"
              data-testid="about-whatsapp-btn"
              className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full font-bold hover:opacity-90 transition-opacity"
              style={{ background: "var(--secondary)", color: "#fff" }}
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp {waNumberDisplay()}
            </a>
          </div>
          <div className="md:col-span-5 rounded-2xl bg-white/10 border border-white/15 p-6 backdrop-blur">
            <div className="text-xs uppercase tracking-widest text-white/70">HQ</div>
            <div className="mt-2 flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 text-white/80" />
              <div>
                <div className="font-display text-lg">Pune, India</div>
                <div className="text-sm text-white/70">Serving Pune now · Metros & rural cities from 01 Oct 2026</div>
              </div>
            </div>
            <div className="mt-5 text-xs uppercase tracking-widest text-white/70">Web</div>
            <div className="text-sm mt-2">www.heylpmate.com</div>
            <div className="mt-5 text-xs uppercase tracking-widest text-white/70">Priority membership</div>
            <div className="text-sm mt-2">₹99/mo — instant WhatsApp connect</div>
            <Link to="/membership" className="inline-block mt-3 text-sm font-bold underline" style={{ color: "var(--secondary)" }}>
              Learn more →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
